import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import type { OrderListItem } from '@/api/admin-orders-types'
import { useUpdateBusinessCard } from '@/api/api-mutations'
import {
	preloadGetCustomer,
	preloadListOrders,
	useGetCustomer,
	useListBusinessCards,
	useListOrders
} from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Prompt, usePrompt } from '@/components/base/prompt'
import { SearchField } from '@/components/base/search-field'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Plus } from '@/icons/plus'
import { XMarkMini } from '@/icons/x-mark-mini'
import { useDebounce } from '@/utils/hooks'
import { ConditionalRouteComponent, PaginatedSearchSchema } from '@/utils/route-utilities'

import { useOrderListColumnDefs } from '../orders/-components'

export const Route = createFileRoute('/_authenticated/_general/customers/$customerUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<CustomerDetail />
		</ConditionalRouteComponent>
	),
	validateSearch: PaginatedSearchSchema,
	loaderDeps: ({ search: { limit, offset, q } }) => ({ limit, offset, q }),
	loader: async ({ context, params: { customerUuid }, deps: { limit, offset, q } }) => {
		preloadListOrders(context, {
			query: { limit, offset, q, customerUuid }
		})
		await preloadGetCustomer(context, { path: { customerUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Customers.Detail.Title()
	}
})

function CustomerDetail() {
	const { L } = useI18n()
	const { customerUuid } = Route.useParams()
	const { limit = DEFAULT_PAGE_SIZE, offset, q } = Route.useSearch()

	const customer = useGetCustomer({ customerUuid })
	const updateCard = useUpdateBusinessCard(false, { customerUuid })

	const orders = useListOrders({ limit, offset, q, customerUuid })
	const orderColumnDefs = useOrderListColumnDefs()

	const { businessCards, loyaltyCards } = useMemo(() => {
		const cards = customer.data?.cards ?? []
		return {
			businessCards: cards.filter(c => c.type === 'BUSINESS'),
			loyaltyCards: cards.filter(c => c.type === 'LOYALTY')
		}
	}, [customer.data?.cards])

	const [assignDialogOpen, setAssignDialogOpen] = useState(false)
	const [cardSearch, setCardSearch] = useState('')
	const debouncedSearch = useDebounce(cardSearch)

	const searchResults = useListBusinessCards(
		{ limit: 10, offset: 0, q: debouncedSearch || undefined, assignedToCustomer: 'false' },
		{ enabled: assignDialogOpen && debouncedSearch.length > 0 }
	)

	const handleAssignCard = async (cardUuid: string) => {
		try {
			const result = await updateCard({
				path: { cardUuid },
				body: { customerUuid }
			})
			if (result.isSuccess) {
				toast.success(L.Customers.Detail.AssignCardSuccess())
				setAssignDialogOpen(false)
				setCardSearch('')
			}
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const prompt = usePrompt()

	const handleUnassignCard = async (cardUuid: string) => {
		const res = await prompt({
			title: L.Customers.Detail.UnassignCard(),
			description: L.Customers.Detail.UnassignCardConfirm(),
			confirmText: L.General.Confirm(),
			cancelText: L.General.Cancel()
		})
		if (!res) return

		try {
			await updateCard({
				path: { cardUuid },
				body: { customerUuid: null }
			})
			toast.success(L.Customers.Detail.UnassignCardSuccess())
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const handleCloseAssignDialog = () => {
		setAssignDialogOpen(false)
		setCardSearch('')
	}

	const displayName = (() => {
		const name = [customer.data?.firstName, customer.data?.lastName].filter(Boolean).join(' ')
		return name || L.Customers.Anonymous()
	})()

	return (
		<>
			<SingleColumnPage hasOutlet>
				{/* General */}
				<Container className='divide-y'>
					<ContainerHeader
						heading={displayName}
						rightSide={
							<Link from={Route.fullPath} to='edit'>
								<IconButton tooltipText={L.General.Edit()}>
									<PencilSquare />
								</IconButton>
							</Link>
						}
					/>
					<SectionRow title={L.Customers.Field.FirstName()} value={customer.data?.firstName ?? '-'} />
					<SectionRow title={L.Customers.Field.LastName()} value={customer.data?.lastName ?? '-'} />
					<SectionRow title={L.Customers.Column.ExternalId()} value={customer.data?.externalId ?? '-'} />
					<SectionRow
						title={L.Fields.CreatedAt()}
						value={customer.data?.createdAt ? new Date(customer.data.createdAt).toLocaleDateString() : '-'}
					/>
					<SectionRow
						title={L.Fields.UpdatedAt()}
						value={customer.data?.updatedAt ? new Date(customer.data.updatedAt).toLocaleDateString() : '-'}
					/>
				</Container>

				{/* Contact Information */}
				<Container className='divide-y'>
					<ContainerHeader heading={L.Customers.Detail.ContactInfo()} />
					<SectionRow title={L.Customers.Column.Email()} value={customer.data?.email ?? '-'} />
					<SectionRow title={L.Customers.Field.Phone()} value={customer.data?.phone ?? '-'} />
					<SectionRow title={L.Customers.Field.SSN()} value={customer.data?.ssn ?? '-'} />
				</Container>

				{/* Business Cards */}
				<Container className='divide-y'>
					<ContainerHeader
						heading={L.BusinessCards.Title()}
						rightSide={
							<IconButton
								tooltipText={L.Customers.Detail.AssignCard()}
								onClick={() => setAssignDialogOpen(true)}
							>
								<Plus />
							</IconButton>
						}
					/>
					{businessCards.length > 0 ? (
						businessCards.map(card => (
							<div key={card.uuid} className='flex items-center justify-between px-6 py-3'>
								<Link
									to='/business-cards/$cardUuid'
									params={{ cardUuid: card.uuid }}
									className='flex flex-1 flex-col'
								>
									<Text size='small' weight='plus'>
										{card.name}
									</Text>
									<Text size='xsmall' className='text-ui-fg-muted'>
										{card.cardNumber}
									</Text>
								</Link>
								<IconButton
									size='xsmall'
									variant='transparent'
									tooltipText={L.Customers.Detail.UnassignCard()}
									onClick={() => handleUnassignCard(card.uuid)}
								>
									<XMarkMini />
								</IconButton>
							</div>
						))
					) : (
						<div className='px-6 py-4'>
							<Text size='small' leading='compact' className='text-ui-fg-muted'>
								{L.General.NoResultsTitle()}
							</Text>
						</div>
					)}
				</Container>

				{/* Loyalty Cards */}
				<Container className='divide-y'>
					<ContainerHeader heading={L.LoyaltyCards.Title()} />
					{loyaltyCards.length > 0 ? (
						loyaltyCards.map(card => (
							<Link
								key={card.uuid}
								to='/loyalty-cards/$cardUuid'
								params={{ cardUuid: card.uuid }}
								className='flex items-center justify-between px-6 py-3'
							>
								<div className='flex flex-1 flex-col'>
									<Text size='small' weight='plus'>
										{card.name}
									</Text>
									<Text size='xsmall' className='text-ui-fg-muted'>
										{card.cardNumber}
									</Text>
								</div>
								<Text size='small' className='text-ui-fg-subtle'>
									{L.LoyaltyCards.Column.AvailablePoints()}:{' '}
									{(card.availablePoints ?? 0).toLocaleString()}
								</Text>
							</Link>
						))
					) : (
						<div className='px-6 py-4'>
							<Text size='small' leading='compact' className='text-ui-fg-muted'>
								{L.General.NoResultsTitle()}
							</Text>
						</div>
					)}
				</Container>

				{/* Orders */}
				<Container className='flex flex-col'>
					<ContainerHeader heading={L.Orders.Title()} rightSide={<SearchField />} />
					<div className='min-w-0 overflow-x-auto'>
						<Table<OrderListItem>
							className='w-full min-w-[600px]'
							data={orders.data?.orders}
							columnUtilities={{
								getCurrencyCode: ({ currencyCode }) => currencyCode
							}}
							columnDefs={orderColumnDefs}
							animateRows
							suppressCellFocus
							domLayout='autoHeight'
							rowToRouterConfig={row => ({
								to: '/orders/$orderUuid',
								params: { orderUuid: row.uuid }
							})}
						/>
					</div>
					<div className='px-3 py-2 sm:px-6 sm:py-4'>
						<Pagination totalCount={orders.data?.count} />
					</div>
				</Container>
			</SingleColumnPage>

			{/* Assign card dialog */}
			<Prompt open={assignDialogOpen} variant='confirmation'>
				<Prompt.Content>
					<Prompt.Header>
						<Prompt.Title>{L.Customers.Detail.AssignCard()}</Prompt.Title>
						<Prompt.Description>{L.Customers.Detail.AssignCardDescription()}</Prompt.Description>
					</Prompt.Header>
					<div className='flex flex-col gap-y-3 border-y border-ui-border-base px-6 py-4'>
						<Input
							autoFocus
							size='small'
							type='search'
							placeholder={L.Customers.Detail.CardNumberPlaceholder()}
							value={cardSearch}
							onChange={e => setCardSearch(e.target.value)}
						/>
						{debouncedSearch.length > 0 && (
							<div className='flex max-h-[200px] flex-col gap-y-1 overflow-y-auto'>
								{searchResults.isLoading ? (
									<Text size='small' leading='compact' className='py-2 text-center text-ui-fg-muted'>
										...
									</Text>
								) : searchResults.data?.cards && searchResults.data.cards.length > 0 ? (
									searchResults.data.cards.map(card => (
										<button
											key={card.uuid}
											type='button'
											className='flex items-center justify-between rounded-md px-3 py-2 text-left transition-colors hover:bg-ui-bg-base-hover'
											onClick={() => handleAssignCard(card.uuid)}
										>
											<div className='flex flex-col'>
												<Text size='small' weight='plus' leading='compact'>
													{card.name}
												</Text>
												<Text size='xsmall' leading='compact' className='text-ui-fg-muted'>
													{card.cardNumber}
												</Text>
											</div>
										</button>
									))
								) : (
									<Text size='small' leading='compact' className='py-2 text-center text-ui-fg-muted'>
										{L.General.NoResultsTitle()}
									</Text>
								)}
							</div>
						)}
					</div>
					<Prompt.Footer>
						<Prompt.Cancel onClick={handleCloseAssignDialog}>{L.General.Cancel()}</Prompt.Cancel>
					</Prompt.Footer>
				</Prompt.Content>
			</Prompt>
		</>
	)
}
